import type { CourseHole } from '@/lib/db/types';
import { displayPlayerName } from '@/lib/display-name';

export interface GroupScorecardGridPlayer {
  id: string;
  name: string;
  guest?: boolean;
  you?: boolean;
  scores: (number | null)[];
}

interface Props {
  holes: CourseHole[];
  players: GroupScorecardGridPlayer[];
  range: { start: number; end: number; indices: number[] };
  highlightPlayerId?: string;
}

function scoreTextClass(score: number | null, par: number): string {
  if (score == null || !Number.isFinite(score)) return 'text-mute';
  const d = score - par;
  if (d < 0) return 'font-bold text-red-600';
  if (d === 0) return 'font-bold text-ink';
  return 'font-bold text-green-700';
}

function sumScores(indices: number[], scores: (number | null)[]): number | null {
  let total = 0;
  let any = false;
  for (const i of indices) {
    const s = scores[i];
    if (s != null && Number.isFinite(s)) {
      total += s;
      any = true;
    }
  }
  return any ? total : null;
}

function sumPar(indices: number[], holes: CourseHole[]): number {
  return indices.reduce((sum, i) => {
    const hole = holes.find((h) => h.hole_num === i + 1);
    return sum + (hole?.par ?? 4);
  }, 0);
}

function ScoreLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] font-semibold text-ink-soft">
      <span className="text-red-600">Red = under par</span>
      <span className="text-ink">Black = par</span>
      <span className="text-green-700">Green = over par</span>
    </div>
  );
}

function GridSection({
  label,
  holeIndices,
  holes,
  players,
  highlightPlayerId,
  totalLabel = 'OUT',
}: {
  label: string;
  holeIndices: number[];
  holes: CourseHole[];
  players: GroupScorecardGridPlayer[];
  highlightPlayerId?: string;
  totalLabel?: 'OUT' | 'IN' | 'TOT';
}) {
  const sidePar = sumPar(holeIndices, holes);
  const nameWidth = players.length >= 4 ? 'w-[72px]' : 'w-[84px]';

  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-line bg-paper">
      <div className="border-b border-line bg-cream-warm px-2.5 py-1.5 text-[10px] font-bold tracking-[0.12em] text-navy">
        {label}
      </div>
      <div className="overflow-x-auto">
        <table className="w-max min-w-full border-collapse text-center text-[11px]">
          <thead>
            <tr className="border-b border-line bg-cream-warm/80">
              <th className={`sticky left-0 z-10 ${nameWidth} border-r border-line bg-cream-warm/95 px-2 py-1.5 text-left text-[9px] font-extrabold tracking-wide text-mute`}>
                HOLE
              </th>
              {holeIndices.map((i) => (
                <th key={`h-${i}`} className="min-w-[28px] px-1 py-1.5 font-mono text-[10px] font-bold text-navy">
                  {i + 1}
                </th>
              ))}
              <th className="min-w-[34px] border-l border-line bg-gold/10 px-1 py-1.5 text-[9px] font-extrabold text-gold-deep">
                {totalLabel}
              </th>
            </tr>
            <tr className="border-b border-line">
              <td className={`sticky left-0 z-10 ${nameWidth} border-r border-line bg-paper px-2 py-1.5 text-left text-[9px] font-extrabold tracking-wide text-mute`}>
                PAR
              </td>
              {holeIndices.map((i) => {
                const par = holes.find((h) => h.hole_num === i + 1)?.par ?? 4;
                return (
                  <td key={`p-${i}`} className="px-1 py-1.5 font-mono text-[10px] font-semibold text-gold-deep">
                    {par}
                  </td>
                );
              })}
              <td className="border-l border-line bg-gold/5 px-1 py-1.5 font-mono text-[10px] font-bold text-navy">
                {sidePar}
              </td>
            </tr>
          </thead>
          <tbody>
            {players.map((player) => {
              const isYou = highlightPlayerId === player.id || player.you;
              const sideTotal = sumScores(holeIndices, player.scores);
              return (
                <tr
                  key={player.id}
                  className={`border-b border-line/70 last:border-b-0 ${isYou ? 'bg-gold/8' : ''}`}
                >
                  <td
                    className={`sticky left-0 z-10 ${nameWidth} border-r border-line px-2 py-1.5 text-left ${
                      isYou ? 'bg-gold/10' : 'bg-paper'
                    }`}
                  >
                    <div className="truncate font-serif text-[11px] font-bold text-navy">
                      {displayPlayerName(player.name, { you: player.you, maxChars: 12 })}
                    </div>
                    {player.guest && (
                      <div className="text-[8px] font-bold tracking-wide text-gold-deep">GUEST</div>
                    )}
                  </td>
                  {holeIndices.map((i) => {
                    const par = holes.find((h) => h.hole_num === i + 1)?.par ?? 4;
                    const score = player.scores[i];
                    return (
                      <td key={`${player.id}-${i}`} className="px-1 py-1.5">
                        <span className={`font-mono text-[11px] ${scoreTextClass(score, par)}`}>
                          {score ?? '—'}
                        </span>
                      </td>
                    );
                  })}
                  <td className="border-l border-line bg-gold/5 px-1 py-1.5 font-mono text-[11px] font-bold text-navy">
                    {sideTotal ?? '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GridSectionBack({
  label,
  holeIndices,
  holes,
  players,
  highlightPlayerId,
  frontIndices,
}: {
  label: string;
  holeIndices: number[];
  holes: CourseHole[];
  players: GroupScorecardGridPlayer[];
  highlightPlayerId?: string;
  frontIndices: number[];
}) {
  const sidePar = sumPar(holeIndices, holes);
  const fullPar = sumPar([...frontIndices, ...holeIndices], holes);
  const nameWidth = players.length >= 4 ? 'w-[72px]' : 'w-[84px]';

  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-line bg-paper">
      <div className="border-b border-line bg-cream-warm px-2.5 py-1.5 text-[10px] font-bold tracking-[0.12em] text-navy">
        {label}
      </div>
      <div className="overflow-x-auto">
        <table className="w-max min-w-full border-collapse text-center text-[11px]">
          <thead>
            <tr className="border-b border-line bg-cream-warm/80">
              <th className={`sticky left-0 z-10 ${nameWidth} border-r border-line bg-cream-warm/95 px-2 py-1.5 text-left text-[9px] font-extrabold tracking-wide text-mute`}>
                HOLE
              </th>
              {holeIndices.map((i) => (
                <th key={`h-${i}`} className="min-w-[28px] px-1 py-1.5 font-mono text-[10px] font-bold text-navy">
                  {i + 1}
                </th>
              ))}
              <th className="min-w-[34px] border-l border-line bg-gold/10 px-1 py-1.5 text-[9px] font-extrabold text-gold-deep">
                IN
              </th>
              <th className="min-w-[38px] border-l border-line bg-navy/10 px-1 py-1.5 text-[9px] font-extrabold text-navy">
                TOT
              </th>
            </tr>
            <tr className="border-b border-line">
              <td className={`sticky left-0 z-10 ${nameWidth} border-r border-line bg-paper px-2 py-1.5 text-left text-[9px] font-extrabold tracking-wide text-mute`}>
                PAR
              </td>
              {holeIndices.map((i) => {
                const par = holes.find((h) => h.hole_num === i + 1)?.par ?? 4;
                return (
                  <td key={`p-${i}`} className="px-1 py-1.5 font-mono text-[10px] font-semibold text-gold-deep">
                    {par}
                  </td>
                );
              })}
              <td className="border-l border-line bg-gold/5 px-1 py-1.5 font-mono text-[10px] font-bold text-navy">
                {sidePar}
              </td>
              <td className="border-l border-line bg-navy/5 px-1 py-1.5 font-mono text-[10px] font-bold text-navy">
                {fullPar}
              </td>
            </tr>
          </thead>
          <tbody>
            {players.map((player) => {
              const isYou = highlightPlayerId === player.id || player.you;
              const sideTotal = sumScores(holeIndices, player.scores);
              const fullTotal = sumScores([...frontIndices, ...holeIndices], player.scores);
              return (
                <tr
                  key={player.id}
                  className={`border-b border-line/70 last:border-b-0 ${isYou ? 'bg-gold/8' : ''}`}
                >
                  <td
                    className={`sticky left-0 z-10 ${nameWidth} border-r border-line px-2 py-1.5 text-left ${
                      isYou ? 'bg-gold/10' : 'bg-paper'
                    }`}
                  >
                    <div className="truncate font-serif text-[11px] font-bold text-navy">
                      {displayPlayerName(player.name, { you: player.you, maxChars: 12 })}
                    </div>
                    {player.guest && (
                      <div className="text-[8px] font-bold tracking-wide text-gold-deep">GUEST</div>
                    )}
                  </td>
                  {holeIndices.map((i) => {
                    const par = holes.find((h) => h.hole_num === i + 1)?.par ?? 4;
                    const score = player.scores[i];
                    return (
                      <td key={`${player.id}-${i}`} className="px-1 py-1.5">
                        <span className={`font-mono text-[11px] ${scoreTextClass(score, par)}`}>
                          {score ?? '—'}
                        </span>
                      </td>
                    );
                  })}
                  <td className="border-l border-line bg-gold/5 px-1 py-1.5 font-mono text-[11px] font-bold text-navy">
                    {sideTotal ?? '—'}
                  </td>
                  <td className="border-l border-line bg-navy/5 px-1 py-1.5 font-mono text-[11px] font-bold text-navy">
                    {fullTotal ?? '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function GroupScorecardGrid({ holes, players, range, highlightPlayerId }: Props) {
  if (!holes.length || !players.length) {
    return (
      <div className="rounded-xl border border-line bg-paper p-3 text-sm text-mute">
        Scorecard grid unavailable for this round.
      </div>
    );
  }

  const chunks: number[][] = [];
  for (let i = 0; i < range.indices.length; i += 9) {
    chunks.push(range.indices.slice(i, i + 9));
  }

  const labels = chunks.length > 1
    ? (range.start === 0 ? ['FRONT 9', 'BACK 9'] : ['HOLES'])
    : [range.indices.length === 9 && range.start === 9 ? 'BACK 9' : 'FRONT 9'];

  const sortedHoles = [...holes].sort((a, b) => a.hole_num - b.hole_num);

  return (
    <div className="space-y-2">
      <ScoreLegend />
      {chunks.length === 2 && range.start === 0 ? (
        <>
          <GridSection
            label={labels[0] ?? 'FRONT 9'}
            holeIndices={chunks[0]}
            holes={sortedHoles}
            players={players}
            highlightPlayerId={highlightPlayerId}
            totalLabel="OUT"
          />
          <GridSectionBack
            label={labels[1] ?? 'BACK 9'}
            holeIndices={chunks[1]}
            holes={sortedHoles}
            players={players}
            highlightPlayerId={highlightPlayerId}
            frontIndices={chunks[0]}
          />
        </>
      ) : (
        <GridSection
          label={labels[0] ?? 'HOLES'}
          holeIndices={chunks[0] ?? range.indices}
          holes={sortedHoles}
          players={players}
          highlightPlayerId={highlightPlayerId}
          totalLabel={range.start === 9 ? 'IN' : 'OUT'}
        />
      )}
    </div>
  );
}
